import React, { useState, useEffect, useCallback } from 'react'
import { Image } from '../types'

interface ImageLightboxProps {
  images: Image[]
  initialIndex: number
  isOpen: boolean
  onClose: () => void
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  images,
  initialIndex,
  isOpen,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [isZoomed, setIsZoomed] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })

  // 重置状态当切换图片或打开时
  useEffect(() => {
    setCurrentIndex(initialIndex)
    setIsZoomed(false)
    setTranslate({ x: 0, y: 0 })
    setImageLoaded(false)
  }, [initialIndex, isOpen])

  // 键盘导航
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          goToPrev()
          break
        case 'ArrowRight':
          goToNext()
          break
        case ' ':
        case 'Enter':
          e.preventDefault()
          toggleZoom()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    // 阻止背景滚动
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isOpen, currentIndex])

  const goToPrev = useCallback(() => {
    if (images.length <= 1) return
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
    resetZoom()
  }, [images.length])

  const goToNext = useCallback(() => {
    if (images.length <= 1) return
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
    resetZoom()
  }, [images.length])

  const toggleZoom = () => {
    if (!imageLoaded) return
    setIsZoomed(!isZoomed)
    setTranslate({ x: 0, y: 0 })
  }

  const resetZoom = () => {
    setIsZoomed(false)
    setTranslate({ x: 0, y: 0 })
    setImageLoaded(false)
  }

  // 拖拽处理（放大后可拖动查看）
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isZoomed) return
    e.preventDefault()
    setDragStart({ x: e.clientX - translate.x, y: e.clientY - translate.y })
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isZoomed || !dragStart) return
    setTranslate({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    })
  }

  const handleMouseUp = () => {
    setDragStart(null)
  }

  // 触摸事件（移动端支持）
  let touchStartX = 0
  let touchStartY = 0

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX = e.touches[0].clientX
    touchStartY = e.touches[0].clientY
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isZoomed) {
      // 放大状态下支持拖动
      const deltaX = e.changedTouches[0].clientX - touchStartX
      const deltaY = e.changedTouches[0].clientY - touchStartY
      setTranslate(prev => ({ x: prev.x + deltaX, y: prev.y + deltaY }))
      return
    }
    
    // 未放大时左右滑动切换图片
    const diffX = e.changedTouches[0].clientX - touchStartX
    const threshold = 50
    
    if (Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        goToPrev()
      } else {
        goToNext()
      }
    }
  }

  if (!isOpen || images.length === 0) return null

  const currentImage = images[currentIndex]

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isZoomed) onClose()
      }}
    >
      {/* 关闭按钮 */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 text-white/80 hover:text-white transition-colors"
        aria-label="关闭"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* 上一张按钮 */}
      {images.length > 1 && (
        <button
          onClick={goToPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/40 text-white hover:bg-black/60 transition-all hover:scale-110 active:scale-95"
          aria-label="上一张"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* 图片区域 */}
      <div
        className={`relative flex items-center justify-center cursor-${isZoomed ? 'grab' : 'zoom-in'} select-none ${
          isZoomed ? 'overflow-hidden' : ''
        }`}
        style={{
          width: isZoomed ? '100vw' : '90vw',
          height: isZoomed ? '100vh' : '85vh',
        }}
        onClick={toggleZoom}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* 加载指示器 */}
        {!imageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        )}

        <img
          src={currentImage.url}
          alt={currentImage.filename}
          onLoad={() => setImageLoaded(true)}
          className={`max-w-full max-h-full object-contain transition-transform duration-200 ease-out ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            transform: isZoomed
              ? `scale(2.5) translate(${translate.x / 2.5}px, ${translate.y / 2.5}px)`
              : 'scale(1)',
            userSelect: 'none' as any,
            // @ts-ignore - WebkitUserDrag is a valid CSS property
            WebkitUserDrag: 'none',
          }}
          draggable={false}
        />
      </div>

      {/* 下一张按钮 */}
      {images.length > 1 && (
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-black/40 text-white hover:bg-black/60 transition-all hover:scale-110 active:scale-95"
          aria-label="下一张"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* 底部信息栏 */}
      <div className="absolute bottom-0 left-0 right-0 z-10 p-6 bg-gradient-to-t from-black/70 to-transparent">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          <div className="text-white min-w-0 flex-1 mr-4">
            <p className="truncate font-medium">{currentImage.filename}</p>
            {isZoomed && (
              <p className="text-sm text-white/60 mt-1">已放大 · 拖动查看 · 点击缩小</p>
            )}
          </div>

          {/* 缩略图导航条 */}
          {images.length > 1 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              {images.map((img, idx) => (
                <button
                  key={img.id}
                  onClick={() => {
                    setCurrentIndex(idx)
                    resetZoom()
                  }}
                  className={`flex-shrink-0 w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
                    idx === currentIndex
                      ? 'border-blue-400 scale-110 shadow-lg shadow-blue-400/30'
                      : 'border-transparent opacity-60 hover:opacity-100 hover:border-white/30'
                  }`}
                >
                  <img
                    src={img.url}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    draggable={false}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 计数器 */}
        {images.length > 1 && (
          <p className="text-center text-white/50 text-sm mt-3">
            {currentIndex + 1} / {images.length}
          </p>
        )}
      </div>

      {/* 操作提示 */}
      {!isZoomed && (
        <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-10 px-4 py-2 bg-black/50 rounded-full text-white/60 text-sm pointer-events-none">
          ← → 切换 · 空格/点击 放大 · Esc 关闭
        </div>
      )}
    </div>
  )
}

export default ImageLightbox
